import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import Modal from 'components/common/Modal';
import TeamVirtualBackgroundContainer from 'team/components/editMember/virtualBackground/TeamVirtualBackgroundContainer';
import TeamVirtualBackgroundPreview from 'team/components/editMember/virtualBackground/TeamVirtualBackgroundPreview';
import { toPng } from 'html-to-image';
import { uploadImageBlob } from 'apis/imageStorageApi';
import { createTeamAsset, TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import { useVirtualBackgroundContext } from 'contexts/VirtualBackgroundContext';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import TeamDarkButtonWithIcon from 'team/shared/components/button/TeamDarkButtonWithIcon';
import { NorthIcon, CloseIcon } from 'icons/materialIcons';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamVirtualBackgroundModal = ({ open, onClose, teamId, onSaved, teamAssets = [] }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [previewReady, setPreviewReady] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [backgroundName, setBackgroundName] = useState('');
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });
    const previewRef = useRef(null);
    const { clearVirtualBackgroundData, backgroundCardState } = useVirtualBackgroundContext();
    const contextId = useMemo(() => `team-virtual-background-${teamId || 'team'}-${Date.now()}`, [teamId]);

    const handleClose = useCallback(() => {
        if (contextId) {
            clearVirtualBackgroundData(contextId);
        }
        onClose();
    }, [contextId, clearVirtualBackgroundData, onClose]);

    const handleSave = useCallback(async () => {
        if (!backgroundName.trim()) {
            setNotification({
                open: true,
                message: 'Background name is required',
                severity: 'info'
            });
            return;
        }
        if (!previewRef.current || !teamId) return;
        if (!previewReady) return;

        const config = backgroundCardState?.[contextId] || {};

        setIsSaving(true);
        try {
            const qrLogoSource = config.virtualBackgroundContextQRCodeLogoUrl || '';
            let qrLogoUrl = '';
            let qrLogoStorageId = '';

            if (qrLogoSource) {
                const trimmedSource = qrLogoSource.trim();
                const isHttpUrl = /^https?:\/\//i.test(trimmedSource);
                const isBlobUrl = /^blob:/i.test(trimmedSource);
                const isDataUrl = /^data:image\/[a-zA-Z]+;base64,/i.test(trimmedSource);

                if (isHttpUrl) {
                    qrLogoUrl = trimmedSource;
                } else if (isBlobUrl || isDataUrl) {
                    try {
                        const logoResponse = await fetch(trimmedSource);
                        const logoBlob = await logoResponse.blob();
                        qrLogoStorageId = `${teamId}-qr-logo-${Date.now()}`;
                        qrLogoUrl = await uploadImageBlob(logoBlob, null, 'team', 'icon', qrLogoStorageId);
                    } catch (logoError) {
                        console.error('Error uploading QR logo for virtual background:', logoError);
                    }
                }
            }

            // Handle original background image if it's a blob or data URL
            let originalBgUrl = config.virtualBackgroundContextBackgroundImageUrl || '';
            let originalBgStorageId = '';
            if (originalBgUrl) {
                const trimmedBgSource = originalBgUrl.trim();
                const isBlobUrl = /^blob:/i.test(trimmedBgSource);
                const isDataUrl = /^data:image\/[a-zA-Z]+;base64,/i.test(trimmedBgSource);

                if (isBlobUrl || isDataUrl) {
                    try {
                        const bgResponse = await fetch(trimmedBgSource);
                        const bgBlob = await bgResponse.blob();
                        originalBgStorageId = `${teamId}-original-bg-${Date.now()}`;
                        originalBgUrl = await uploadImageBlob(bgBlob, null, 'team', 'background', originalBgStorageId);
                    } catch (bgError) {
                        console.error('Error uploading original background for virtual background asset:', bgError);
                    }
                }
            }

            await new Promise((resolve) => requestAnimationFrame(resolve));
            await new Promise((resolve) => requestAnimationFrame(resolve));
            const scale = 4;
            const dataUrl = await toPng(previewRef.current, {
                width: previewRef.current.offsetWidth * scale,
                height: previewRef.current.offsetHeight * scale,
                style: {
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: `${previewRef.current.offsetWidth}px`,
                    height: `${previewRef.current.offsetHeight}px`,
                },
                quality: 1.0,
            });
            const blob = await (await fetch(dataUrl)).blob();
            const storageId = `${teamId}-${Date.now()}`;
            const fileUrl = await uploadImageBlob(blob, null, 'team', 'background', storageId);
            const response = await createTeamAsset(teamId, {
                type: TEAM_ASSET_TYPES.VIRTUAL_BACKGROUND,
                url: fileUrl,
                name: backgroundName.trim(),
                metadata: {
                    storageId,
                    storageSubType: 'background',
                    backgroundName: backgroundName.trim(),
                    qrLogoUrl: qrLogoUrl || undefined,
                    qrLogoStorageId: qrLogoStorageId || undefined,
                    // Save full configuration for future editing
                    originalBackgroundImageUrl: originalBgUrl,
                    originalBgStorageId: originalBgStorageId || undefined,
                    showQRCode: config.virtualBackgroundContextShowQRCode,
                    showCardName: config.virtualBackgroundContextShowCardName,
                    showCompany: config.virtualBackgroundContextShowCompany,
                    showJobTitle: config.virtualBackgroundContextShowJobTitle,
                    showLocation: config.virtualBackgroundContextShowLocation,
                    qrCodeColor: config.virtualBackgroundContextQRCodeColor,
                    condensedView: config.virtualBackgroundContextCondensedView
                }
            });
            if (onSaved) {
                onSaved(response);
            }
            handleClose();
        } catch (error) {
            console.error('Error saving virtual background:', error);
        } finally {
            setIsSaving(false);
        }
    }, [backgroundName, teamId, onSaved, handleClose, backgroundCardState, contextId, previewReady]);

    useEffect(() => {
        if (open) {
            setCurrentPage(1);
            setBackgroundName('');
        }
    }, [open]);

    const handlePaginationInfo = useCallback((info) => {
        setTotalPages(info.totalPages || 1);
        if (info.currentPage && info.currentPage !== currentPage) {
            setCurrentPage(info.currentPage);
        }
    }, [currentPage]);

    const canPrev = currentPage > 1;
    const canNext = currentPage < totalPages;

    const handleNotificationClose = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    return (
        <Modal open={open} onClose={handleClose} width="70%" maxWidth={1000} height="90%">
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            height: '100%',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#ffffff',
                        }}
                    >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Box sx={{ padding: '1.5rem 1.5rem 0' }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        color: '#4f4f4f',
                                        marginBottom: '1rem'
                                    }}
                                >
                                    Create Virtual Background
                                </Typography>
                                <Box sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: '#4f4f4f',
                                            marginBottom: '10px'
                                        }}
                                    >
                                        Background Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Background Name"
                                        value={backgroundName}
                                        onChange={(event) => setBackgroundName(event.target.value)}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    border: 'none',
                                                },
                                                '&:hover fieldset': {
                                                    border: 'none',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: 'none',
                                                },
                                                '& input::placeholder': {
                                                    fontSize: '0.8rem',
                                                    fontWeight: '300',
                                                },
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem', marginBottom: '1.5rem' }}>
                                <TeamVirtualBackgroundContainer
                                    contextId={contextId}
                                    enableCardFetch={false}
                                    externalPage={currentPage}
                                    onExternalPageChange={setCurrentPage}
                                    onPaginationInfo={handlePaginationInfo}
                                    hidePagination={true}
                                    hideHeading={true}
                                    showTeamAssetsTab={true}
                                    teamAssets={teamAssets}
                                    qrColorRowWidth="100%"
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    padding: '0 1.5rem 1.5rem'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TeamWhiteButtonWithIcon
                                        text="Prev"
                                        icon={NorthIcon}
                                        iconProps={{ transform: 'rotate(-90deg)' }}
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={!canPrev}
                                    />
                                    <TeamWhiteButtonWithIcon
                                        text="Next"
                                        icon={NorthIcon}
                                        iconProps={{ transform: 'rotate(90deg)' }}
                                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                        disabled={!canNext}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TeamWhiteButtonWithIcon text="Cancel" onClick={handleClose} />
                                    <TeamDarkButtonWithIcon
                                        text={isSaving ? 'Saving...' : 'Save'}
                                        onClick={handleSave}
                                        disabled={
                                            isSaving ||
                                            !previewReady ||
                                            !backgroundCardState?.[contextId]?.virtualBackgroundContextBackgroundImageUrl
                                        }
                                    />
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                width: '40%',
                                minWidth: '360px',
                                borderLeft: '1px solid #e0e0e0',
                                padding: 1.5,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 2, ml: 2 }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#4f4f4f' }}>
                                    Virtual background preview
                                </Typography>
                                <Box component="button"
                                    onClick={handleClose}
                                    sx={{
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: 0
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: '1.5rem', color: '#828282' }} />
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Box sx={{ width: '100%', transform: 'translateY(-0.75rem)' }}>
                                    <TeamVirtualBackgroundPreview
                                        contextId={contextId}
                                        containerRef={previewRef}
                                        hideDownload={true}
                                        onPreviewReady={setPreviewReady}
                                        hideTitle={true}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                handleClose={handleNotificationClose}
                severity={notification.severity}
            />
        </Modal>
    );
};

export default TeamVirtualBackgroundModal;
